import { useAuthorization } from 'components/Authorization';
import { fetchFlagBatch, FlagKey, Response } from 'services/api/flagr';
import { useSetOrgConfigStateValue } from './business/useOrgConfig';
import { useMemoizedFn } from 'ahooks';
import { updateNotifyInterval } from 'components/Nim';
import { setMaxConversationsLimit } from './business/lruConversation';

export const useBatchFlagr = () => {
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const setOrgConfig = useSetOrgConfigStateValue();

  return useMemoizedFn(() => {
    const orgId = payload?.orgId;
    const id = payload?.id;

    if (!orgId || !id) return;

    const base = { orgId } as const;

    const userParams = { ...base, userId: id };

    fetchFlagBatch([
      [FlagKey.RefundEnable, base],
      [FlagKey.ReviewEnable, base],
      [FlagKey.DashboardPerfEnable, base],
      [FlagKey.XiapibuyEnable, userParams],
      [FlagKey.ShopeeImageDomainScore, userParams],
      [FlagKey.NotifyInterval, userParams],
      [FlagKey.PaySupport, base],
      [FlagKey.TiktokImageProxyOriginList, userParams],
      [FlagKey.MercadoSyncMessageSeconds, base],
      [FlagKey.ConversationLruLimit, userParams],
    ])
      .then((data) => {
        if (data) {
          const [
            refundEnable,
            reviewEnable,
            dashboardPerfEnable,
            xiapibuyEnable,
            shopeeImageDomainScore,
            notifyInterval,
            paySupport,
            tiktokImageProxyOriginList,
            mercadoSyncMessageSeconds,
            conversationLruLimit,
          ] = data;

          const { interval = 5 * 1000 } =
            (notifyInterval as Response[FlagKey.NotifyInterval]) || {};

          updateNotifyInterval(interval);

          const lruConversationLimit = (
            conversationLruLimit as Response[FlagKey.ConversationLruLimit]
          )?.limit;
          if (lruConversationLimit) {
            setMaxConversationsLimit(lruConversationLimit);
          }

          return {
            refundEnable: (refundEnable as Response[FlagKey.RefundEnable])?.enable ?? false,
            reviewEnable: (reviewEnable as Response[FlagKey.ReviewEnable])?.enable ?? false,
            robotIndependentEnable:
              (dashboardPerfEnable as Response[FlagKey.DashboardPerfEnable])?.allowed ?? false,
            perfNavEnable:
              (dashboardPerfEnable as Response[FlagKey.DashboardPerfEnable])?.perfNav ?? false,
            xiapibuyEnable: (xiapibuyEnable as Response[FlagKey.XiapibuyEnable])?.enable,
            shopeeImageDomainScoreEnable:
              (shopeeImageDomainScore as Response[FlagKey.ShopeeImageDomainScore])?.enable || false,
            shopeeImageReportAllEnable:
              (shopeeImageDomainScore as Response[FlagKey.ShopeeImageDomainScore])
                ?.reportAllEnable ?? false,
            paySupport: paySupport as Response[FlagKey.PaySupport],
            tiktokImageProxyOriginList: (
              tiktokImageProxyOriginList as Response[FlagKey.TiktokImageProxyOriginList]
            )?.originList,
            mercadoSyncMessageSeconds: (
              mercadoSyncMessageSeconds as Response[FlagKey.MercadoSyncMessageSeconds]
            )?.seconds,
          };
        } else {
          return {};
        }
      })
      .then((config) => {
        setOrgConfig((preConfig) => {
          return {
            ...preConfig,
            ...config,
          };
        });
      });
  });
};
